import {BasePopup} from "components/popup/BasePopup"
import styles from "./BatchDailyAttendanceCheckin.module.css"
import {BaseButton, BaseLoading, KlassDropdown} from "components"
import {Icon} from "components/Icon"
import {BaseTimePicker} from "components/DateTimePicker"
import moment, {Moment} from "moment"
import {useModel, useMutation} from "hooks"
import {useEffect, useMemo, useRef, useState, Fragment} from "react"
import {Checkbox} from "antd"
import cx from "classnames"
import {get} from "lodash"
import {handleError, toastError, toastInfo, toastSuccess, getFieldLabel} from "helpers"
import {attendanceService} from "services"
import {AttendanceEvent, AttendanceEventType} from "types/attendance"
import {useAttendanceCheckBreakEventSetting} from "hooks/useAttendanceCheckBreakEventSetting"
import {Settings} from "types/settings"

const EVENT_OPTIONS: Array<{name: string; value: AttendanceEventType}> = [
    {name: "Checkin", value: AttendanceEventType.Checkin},
    {name: "Checkout", value: AttendanceEventType.Checkout},
    {name: "In", value: AttendanceEventType.BreakEnd},
    {name: "Out", value: AttendanceEventType.BreakStart}
]

type AttendanceItem = {
    attendanceId: number
    events: AttendanceEvent[]
}

type BatchRowResult = {
    status: "success" | "skipped" | "failed"
    message: string
}

type Props = {
    isShow: boolean
    isFetching: boolean
    students: any[]
    date: string
    onClose: () => void
    onSuccess: () => void
}

export function BatchDailyAttendanceCheckin(props: Props) {
    const {isShow, onClose, isFetching, date, students: initialStudents, onSuccess} = props
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const timeFormat = model.getUserTimeFormat()
    const {isBreakEnabled} = useAttendanceCheckBreakEventSetting()

    const [selectedTime, setSelectedTime] = useState<Moment | null>(moment())

    const [selectedEventType, setSelectedEventType] = useState<{name: string; value: AttendanceEventType} | null>(null)
    const [students, setStudents] = useState(initialStudents)
    const wasOpenRef = useRef(false)
    const batchResultsRef = useRef<Record<number, BatchRowResult>>({})

    const eventTypeOptions = useMemo(() => {
        if (isBreakEnabled) {
            return EVENT_OPTIONS
        }
        return EVENT_OPTIONS.filter(
            (item) => ![AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.value)
        )
    }, [isBreakEnabled])

    useEffect(() => {
        if (isShow && !isFetching && initialStudents.length <= 0) {
            toastInfo("There are no students to update")
            onClose()
        }
    }, [isShow, isFetching, initialStudents, onClose])

    useEffect(() => {
        if (!isShow) return

        const justOpened = !wasOpenRef.current
        wasOpenRef.current = true

        if (justOpened) {
            setSelectedTime(moment())
            setSelectedEventType(null)
            batchResultsRef.current = {}
        }

        const resultsMap = batchResultsRef.current
        setStudents(
            initialStudents.map((item) => ({
                ...item,
                isChecked: false,
                isExpanded: !!(item.attendances ?? []).length,
                batchResult: resultsMap[item.profileId] ?? null
            }))
        )
    }, [isShow, initialStudents])

    useEffect(() => {
        if (!isShow) {
            wasOpenRef.current = false
            batchResultsRef.current = {}
        }
    }, [isShow])

    const selectedCount = useMemo(() => students.filter((item) => item.isChecked).length, [students])

    const toggleStudent = (profileId: number, checked: boolean) => {
        setStudents((prev) => prev.map((item) => (item.profileId === profileId ? {...item, isChecked: checked} : item)))
    }

    const toggleAll = (checked: boolean) => {
        setStudents((prev) => prev.map((item) => ({...item, isChecked: checked})))
    }

    const toggleExpand = (profileId: number) => {
        setStudents((prev) =>
            prev.map((item) => (item.profileId === profileId ? {...item, isExpanded: !item.isExpanded} : item))
        )
    }

    const onSubmit = () => {
        if (!selectedTime) {
            toastError("Please select a time")
            return
        }

        if (!selectedEventType?.value) {
            toastError("Please select an event type")
            return
        }

        const selectedStudents = students.filter((item) => item.isChecked)
        if (!selectedStudents.length) {
            toastError("Please select at least one student")
            return
        }

        const filterDate = moment(date)
        const baseTime = moment(selectedTime).set({
            date: filterDate.date(),
            month: filterDate.month(),
            year: filterDate.year(),
            seconds: 0,
            milliseconds: 0
        })

        const payloadStudents = selectedStudents
            .map((item) => {
                const campusId = get(item, ["student", "campuses", 0, "id"])
                const majorVersionId = get(item, ["student", "currentMajorVersionId", "id"])
                const timezone = get(item, ["student", "timezone"]) ?? moment.tz.guess()
                const profileId = item.profileId ?? item.student?.id

                if (!campusId || !profileId) {
                    return null
                }

                return {
                    profileId,
                    campusId,
                    majorVersionId,
                    eventType: selectedEventType.value,
                    timezone,
                    isMakeup: !!item.isMakeup,
                    createdAt: moment(baseTime).tz(timezone, true).toISOString()
                }
            })
            .filter(Boolean)

        if (!payloadStudents.length) {
            toastError("Selected students are missing campus information")
            return
        }

        batchCreateMutation.mutate({
            createdAt: baseTime.toISOString(),
            message: "Batch check-in/out from roll call",
            students: payloadStudents as any
        })
    }

    const batchCreateMutation = useMutation(attendanceService.batchCreateDailyAttendanceEvents, {
        onSuccess: (response) => {
            const data = response?.data ?? response
            const successCount = data?.successCount ?? 0
            const failedCount = data?.failedCount ?? 0
            const skippedCount = data?.skippedCount ?? 0
            const results = data?.results ?? []

            const nextResults: Record<number, BatchRowResult> = {...batchResultsRef.current}
            results.forEach((result: any) => {
                if (!result?.profileId) return
                if (result.skipped) {
                    nextResults[result.profileId] = {
                        status: "skipped",
                        message: result.skipReason || "Skipped"
                    }
                } else if (!result.success) {
                    nextResults[result.profileId] = {
                        status: "failed",
                        message: result.error || "Failed"
                    }
                } else {
                    nextResults[result.profileId] = {
                        status: "success",
                        message: "Updated"
                    }
                }
            })
            batchResultsRef.current = nextResults
            setStudents((prev) =>
                prev.map((item) => ({
                    ...item,
                    isChecked: false,
                    batchResult: nextResults[item.profileId] ?? item.batchResult ?? null
                }))
            )

            if (failedCount > 0 && (successCount > 0 || skippedCount > 0)) {
                toastInfo(`Updated ${successCount}. Skipped ${skippedCount}. Failed ${failedCount}. See Result column.`)
            } else if (failedCount > 0) {
                toastError(`Failed ${failedCount} student(s). See Result column.`)
            } else if (skippedCount > 0 && successCount === 0) {
                toastInfo(`Skipped ${skippedCount} student(s). See Result column.`)
            } else if (skippedCount > 0) {
                toastInfo(`Updated ${successCount}. Skipped ${skippedCount}. See Result column.`)
            } else {
                toastSuccess("Batch check-in/out successfully")
            }
            onSuccess()
        },
        onError: (error) => {
            handleError(error)
        }
    })

    const renderBatchResult = (batchResult?: BatchRowResult | null) => {
        if (!batchResult) {
            return <span className={styles.resultEmpty}>—</span>
        }
        return (
            <span
                className={cx(styles.resultBadge, {
                    [styles.resultSuccess]: batchResult.status === "success",
                    [styles.resultSkipped]: batchResult.status === "skipped",
                    [styles.resultFailed]: batchResult.status === "failed"
                })}
                title={batchResult.message}>
                {batchResult.message}
            </span>
        )
    }

    const renderAttendanceEventsPanel = (item: any) => {
        const attendances: AttendanceItem[] = item.attendances ?? []
        if (!item.isExpanded || !attendances.length) {
            return null
        }

        const timezone = get(item, ["student", "timezone"]) ?? moment.tz.guess()

        return (
            <div className={styles.expandedAttendancePanel}>
                <div className={styles.eventsContainer}>
                    {attendances.map((attendance, attendanceIndex) => (
                        <Fragment key={attendance.attendanceId ?? attendanceIndex}>
                            {attendance.events?.map((event, eventIndex) => (
                                <div
                                    key={event.id ?? `${attendance.attendanceId ?? attendanceIndex}-${eventIndex}`}
                                    className={styles.eventItem}>
                                    <div className={styles.event}>
                                        {event.eventType === AttendanceEventType.Checkin ? (
                                            <Icon icon="CHECKIN_FILL" className={styles.eventIcon} color="#18a957" />
                                        ) : null}
                                        {event.eventType === AttendanceEventType.Checkout ? (
                                            <Icon icon="CHECKOUT_FILL" className={styles.eventIcon} color="#DF1642" />
                                        ) : null}
                                        {event.eventType === AttendanceEventType.BreakEnd ? (
                                            <Icon icon="ATTENDANCE_IN" className={styles.eventIcon} color="#18a957" />
                                        ) : null}
                                        {event.eventType === AttendanceEventType.BreakStart ? (
                                            <Icon icon="ATTENDANCE_OUT" className={styles.eventIcon} color="#DF1642" />
                                        ) : null}
                                        <span className={styles.eventTime}>
                                            {moment(event.createdAt).tz(timezone).format(timeFormat)}
                                        </span>
                                    </div>
                                    {eventIndex !== attendance.events?.length - 1 ? (
                                        <div className={styles.eventSeparator} />
                                    ) : null}
                                </div>
                            ))}
                            {attendanceIndex !== attendances.length - 1 ? (
                                <div className={styles.eventSeparator} />
                            ) : null}
                        </Fragment>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <BasePopup isShow={isShow} onClose={onClose} width="90vw" leftIcon="PERSON_ADD" destroyOnClose keyboard={false}>
            <div className={styles.container}>
                <div className={styles.formTitle}>Batch Check-In / Check-Out on {moment(date).format(dateFormat)}</div>
                <div className={styles.timeRow}>
                    <span className={styles.timeLabel}>
                        Time<span className={styles.star}>*</span>
                    </span>
                    <BaseTimePicker
                        style={{width: 160}}
                        value={selectedTime}
                        format={timeFormat}
                        allowClear={false}
                        onChange={(value) => setSelectedTime(value)}
                        placeholder="Time"
                    />
                    <span className={styles.timeLabel}>
                        Event Type<span className={styles.star}>*</span>
                    </span>
                    <div className={styles.eventTypeSelect}>
                        <KlassDropdown
                            options={eventTypeOptions}
                            value={selectedEventType}
                            valueKey="value"
                            labelKey="name"
                            placeholder="Select event type"
                            isClearable={false}
                            onChange={(option) => setSelectedEventType(option)}
                        />
                    </div>
                </div>
                <div className={styles.tableContainer}>
                    <div className={styles.rowHeader}>
                        <span className={styles.headerTitle} />
                        <span className={cx(styles.headerTitle, styles.headerCenter)}>
                            <Checkbox
                                checked={students.length > 0 && selectedCount === students.length}
                                indeterminate={selectedCount > 0 && selectedCount < students.length}
                                onChange={(event) => toggleAll(event.target.checked)}
                            />
                        </span>
                        <span className={styles.headerTitle}>
                            {getFieldLabel(model, Settings.GeneralLabel.EnrollmentId, "Enrollment Id")}
                        </span>
                        <span className={styles.headerTitle}>User Id</span>
                        <span className={styles.headerTitle}>Student</span>
                        <span className={styles.headerTitle}>Phase</span>
                        <span className={styles.headerTitle}>Shift</span>
                        <span className={styles.headerTitle}>Major</span>
                        <span className={styles.headerTitle}>Major Version</span>
                        <span className={styles.headerTitle}>Result</span>
                    </div>
                    <div className={styles.tableBody}>
                        {students.map((item, index) => {
                            const hasAttendances = !!(item.attendances ?? []).length
                            return (
                                <div
                                    key={item.profileId ?? index}
                                    className={cx(styles.rowGroup, {[styles.oddRow]: index % 2 === 1})}>
                                    <div className={styles.row}>
                                        <div className={styles.expandCell}>
                                            {hasAttendances ? (
                                                <button
                                                    type="button"
                                                    className={styles.expandBtn}
                                                    onClick={() => toggleExpand(item.profileId)}>
                                                    <Icon
                                                        icon={item.isExpanded ? "ARROW_DOWN" : "ARROW_RIGHT"}
                                                        className={styles.arrowIcon}
                                                    />
                                                </button>
                                            ) : null}
                                        </div>
                                        <div className={styles.checkbox}>
                                            <Checkbox
                                                checked={!!item.isChecked}
                                                onChange={(event) =>
                                                    toggleStudent(item.profileId, event.target.checked)
                                                }
                                            />
                                        </div>
                                        <div className={styles.col}>{item.customProfileId || "—"}</div>
                                        <div className={styles.col}>{item.userId ?? "—"}</div>
                                        <div className={styles.col}>{item.studentName}</div>
                                        <div className={styles.col}>{item.phaseNames}</div>
                                        <div className={styles.col}>{item.shiftNames}</div>
                                        <div className={styles.col}>{item.majorHtml}</div>
                                        <div className={styles.col}>{item.majorVersionHtml}</div>
                                        <div className={styles.col}>{renderBatchResult(item.batchResult)}</div>
                                    </div>
                                    {renderAttendanceEventsPanel(item)}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className={styles.actionWrap}>
                    <BaseButton
                        title="Cancel"
                        onClick={onClose}
                        variant="secondary"
                        loading={batchCreateMutation.isLoading}
                    />
                    <BaseButton
                        title={`Submit (${selectedCount})`}
                        onClick={onSubmit}
                        disabled={selectedCount <= 0}
                        loading={batchCreateMutation.isLoading}
                    />
                </div>
            </div>
            <BaseLoading isShow={isFetching || batchCreateMutation.isLoading} />
        </BasePopup>
    )
}
